import React, { useRef, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MapRegion } from '../../types/MapTypes';
import { RS_GEOJSON } from '../../data/rsGeoJson';
import { RS_MESORREGIAO_GEOJSON } from '../../data/rsMesorregiaoGeoJson';
import citiesWithCoordinates from '../../data/cities_with_coordinates.json';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';

interface CanvasMapProps {
  region: MapRegion;
  onCityClick?: (cityName: string, properties: any) => void;
  onSideSheetChange?: (isOpen: boolean) => void;
  onRegionZoomChange?: (isZoomed: boolean) => void;
  showCities?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  fillColor?: string;
  cityColor?: string;
  customRegionsGeoJSON?: any;
  useCustomRegions?: boolean;
  triggerCityId?: number | null;
  regionProjections?: Record<string, number>;
  totalProjection?: number;
  loadingProjections?: boolean;
  nextElectionYear?: number | null;
  showProjections?: boolean;
  canViewElectionResults?: boolean;
}

interface CityMarker {
  name: string;
  x: number;
  y: number;
  properties: any;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  name: string;
  population: number;
}

interface RegionTooltipState {
  visible: boolean;
  x: number;
  y: number;
  name: string;
}

interface PastCampaign {
  year: number;
  votes: number;
}

interface CityDetails {
  name: string;
  population: number;
  pastCampaigns: PastCampaign[];
  projectedVotes: number;
  responsible: string | null;
  responsibleId: number | null;
  responsibleEmail: string | null;
  region: string;
  cityId: number;
}

interface Bounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 500px;
  position: relative;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  cursor: default;
`;

const CityTooltip = styled.div<{ $visible: boolean; $x: number; $y: number }>`
  position: absolute;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  transform: translate(-50%, calc(-100% - 15px));
  background: white;
  padding: 10px 14px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease;
  z-index: 1000;
  min-width: 120px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid white;
  }
  
  h4 {
    margin: 0 0 4px 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }
  
  p {
    margin: 0;
    font-size: 12px;
    color: #64748b;
  }
`;

const RegionTooltip = styled.div<{ $visible: boolean; $x: number; $y: number }>`
  position: absolute;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  transform: translate(-50%, calc(-100% - 10px));
  background: rgba(30, 41, 59, 0.95);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease;
  z-index: 999;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgba(30, 41, 59, 0.95);
  }
`;

const SideSheet = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 400px;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;
  z-index: 2000;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const SideSheetHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const SideSheetContent = styled.div`
  padding: 24px;
  flex: 1;
`;

const CityTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
`;

const CitySubtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  color: #64748b;
`;

const InfoSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const InfoValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const InfoCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StatCard = styled.div`
  background: var(--accent);
  border-radius: 8px;
  padding: 16px;
  color: white;
  
  &:nth-child(2) {
    background: var(--accent);
  }
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
`;

const ResponsibleCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    border-color: var(--accent);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  color: white;
`;

const ResponsibleInfo = styled.div`
  flex: 1;
`;

const ResponsibleName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
`;

const ResponsibleRole = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const SearchContainer = styled.div`
  position: absolute;
  top: 145px;
  left: 40px;
  z-index: 220;
  width: 320px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: #94a3b8;
  transition: color 0.2s ease;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 20px 16px 52px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 50px;
  font-size: 15px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(var(--accent-rgb), 0.3);
    box-shadow: 
      0 8px 30px rgba(0, 0, 0, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 1);
  }
  
  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(var(--accent-rgb), 0.5);
    box-shadow: 
      0 12px 40px rgba(var(--accent-rgb), 0.15),
      0 4px 12px rgba(var(--accent-rgb), 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    
    & + ${SearchIcon} {
      color: var(--accent);
    }
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const SearchResults = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-height: 320px;
  overflow-y: auto;
  display: ${props => props.$visible ? 'block' : 'none'};
  z-index: 230;
`;

const SearchResultItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f8fafc;
  }
  
  .city-name {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 2px;
  }
  
  .city-info {
    font-size: 12px;
    color: #64748b;
  }
`;

const NoResults = styled.div`
  padding: 16px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
`;

const ResetButton = styled.button<{ $visible: boolean }>`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  z-index: 100;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'all' : 'none'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  
  svg {
    width: 20px;
    height: 20px;
    color: #475569;
    transition: all 0.3s ease;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(var(--accent-rgb), 0.3);
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 12px 40px rgba(var(--accent-rgb), 0.15);
    
    svg {
      color: var(--accent);
      transform: rotate(180deg);
    }
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

const CloseRegionButton = styled.button<{ $visible: boolean }>`
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  border: 2px solid #e2e8f0;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  z-index: 150;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'all' : 'none'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: #f8fafc;
    border-color: var(--accent);
    color: var(--accent);
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  .icon {
    font-size: 18px;
  }
`;

const shimmer = `
  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const ShimmerBlock = styled.div<{ $width?: string; $height?: string; $radius?: string }>` 
  width: ${p => p.$width || '100%'};
  height: ${p => p.$height || '16px'};
  border-radius: ${p => p.$radius || '6px'};
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 800px 100%;
  animation: shimmer 1.4s infinite linear;
  ${shimmer}
`;

const BusinessIndicators = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(226, 232, 240, 0.6);
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  z-index: 100;
  min-width: 320px;
  max-width: 400px;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'all' : 'none'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }
`;

const IndicatorsTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 20px;
    height: 20px;
    color: var(--accent);
  }
`;

const TotalVotes = styled.div`
  background: var(--accent);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  color: white;
`;

const TotalVotesLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.9;
  margin-bottom: 6px;
`;

const TotalVotesValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
`;

const RegionVotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.4);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.7);
  }
`;

const RegionVoteItem = styled.div<{ $color: string; $isHovered: boolean; $isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: ${props => props.$isSelected ? props.$color.replace('0.15', '0.12') : 'rgba(248, 250, 252, 0.8)'};
  border: 1px solid ${props => props.$isSelected ? props.$color.replace('0.15', '0.3') : 'rgba(226, 232, 240, 0.5)'};
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  transform: ${props => props.$isHovered ? 'translateX(4px)' : 'translateX(0)'};
  box-shadow: ${props => props.$isHovered ? '0 2px 8px rgba(0, 0, 0, 0.08)' : 'none'};
  
  &:hover {
    border-color: ${props => props.$color.replace('0.15', '0.5')};
    background: ${props => props.$color.replace('0.15', '0.08')};
  }
`;

const RegionVoteName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RegionColorDot = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$color.replace('0.15', '0.8')};
  flex-shrink: 0;
`;

const RegionVoteValue = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
`;

const DEFAULT_BOUNDS: Bounds = {
  north: -27.0,
  south: -33.8,
  east: -49.5,
  west: -57.7
};

// Region colors based on mesorregiao
// Paleta de cores para regiões customizadas
const CUSTOM_REGION_COLORS = [
  'rgba(37, 99, 235, 0.15)',    // Blue
  'rgba(16, 185, 129, 0.15)',   // Green
  'rgba(245, 158, 11, 0.15)',   // Orange
  'rgba(139, 92, 246, 0.15)',   // Purple
  'rgba(236, 72, 153, 0.15)',   // Pink
  'rgba(239, 68, 68, 0.15)',    // Red
  'rgba(20, 184, 166, 0.15)',   // Teal
  'rgba(251, 191, 36, 0.15)',   // Amber
  'rgba(168, 85, 247, 0.15)',   // Violet
  'rgba(34, 197, 94, 0.15)',    // Emerald
];

const REGION_COLORS: { [key: string]: string } = {
  'Noroeste Rio-grandense': 'rgba(37, 99, 235, 0.15)',       // Blue
  'Nordeste Rio-grandense': 'rgba(16, 185, 129, 0.15)',      // Green
  'Centro Ocidental Rio-grandense': 'rgba(245, 158, 11, 0.15)', // Orange
  'Centro Oriental Rio-grandense': 'rgba(139, 92, 246, 0.15)',  // Purple
  'Metropolitana de Porto Alegre': 'rgba(236, 72, 153, 0.15)',  // Pink
  'Sudoeste Rio-grandense': 'rgba(239, 68, 68, 0.15)',       // Red
  'Sudeste Rio-grandense': 'rgba(20, 184, 166, 0.15)',       // Teal
};

const REGION_BORDER_COLORS: { [key: string]: string } = {
  'Noroeste Rio-grandense': 'rgba(37, 99, 235, 0.15)',       // Same as background
  'Nordeste Rio-grandense': 'rgba(16, 185, 129, 0.15)',      // Same as background
  'Centro Ocidental Rio-grandense': 'rgba(245, 158, 11, 0.15)', // Same as background
  'Centro Oriental Rio-grandense': 'rgba(139, 92, 246, 0.15)',  // Same as background
  'Metropolitana de Porto Alegre': 'rgba(236, 72, 153, 0.15)',  // Same as background
  'Sudoeste Rio-grandense': 'rgba(239, 68, 68, 0.15)',       // Same as background
  'Sudeste Rio-grandense': 'rgba(20, 184, 166, 0.15)',       // Same as background
};

// Mock projected votes by region
const REGION_PROJECTED_VOTES: { [key: string]: number } = {
  'Noroeste Rio-grandense': 285000,
  'Nordeste Rio-grandense': 420000,
  'Centro Ocidental Rio-grandense': 195000,
  'Centro Oriental Rio-grandense': 340000,
  'Metropolitana de Porto Alegre': 1250000,
  'Sudoeste Rio-grandense': 230000,
  'Sudeste Rio-grandense': 380000,
};

const CanvasMap: React.FC<CanvasMapProps> = ({
  region,
  onCityClick,
  onSideSheetChange,
  onRegionZoomChange,
  showCities = true,
  backgroundColor = '#ffffff',
  borderColor = '#1e3a5f',
  fillColor = 'rgba(30, 58, 95, 0.1)',
  cityColor = '#2563eb',
  customRegionsGeoJSON,
  useCustomRegions = false,
  triggerCityId = null,
  regionProjections,
  totalProjection,
  loadingProjections = false,
  nextElectionYear = null,
  showProjections = true,
  canViewElectionResults = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [cityMarkers, setCityMarkers] = useState<CityMarker[]>([]);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    name: '',
    population: 0
  });
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [regionTooltip, setRegionTooltip] = useState<RegionTooltipState>({
    visible: false,
    x: 0,
    y: 0,
    name: ''
  });
  const [selectedCityName, setSelectedCityName] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedCity, setSelectedCity] = useState<CityDetails | null>(null);
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<typeof citiesWithCoordinates>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isEditingProjection, setIsEditingProjection] = useState(false);
  const [editedProjection, setEditedProjection] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [regionOpacities, setRegionOpacities] = useState<{ [key: string]: number }>({});
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [isAssigningResponsible, setIsAssigningResponsible] = useState(false);
  const [isLoadingResponsible, setIsLoadingResponsible] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const voronoiCacheRef = useRef<{
    key: string;
    regionMap: Int16Array;
    regionMeta: { regionName: string; colorIndex: number }[];
    ofw: number;
    ofh: number;
  } | null>(null);
  const navigate = useNavigate();
  const { session, isAdmin } = useAuth();

  const bounds = region.bounds || DEFAULT_BOUNDS;

  // Notify parent when side sheet state changes
  useEffect(() => {
    if (onSideSheetChange) {
      onSideSheetChange(isSideSheetOpen);
    }
  }, [isSideSheetOpen, onSideSheetChange]);

  // Notify parent when region zoom changes
  useEffect(() => {
    if (onRegionZoomChange) {
      onRegionZoomChange(selectedRegion !== null);
    }
  }, [selectedRegion, onRegionZoomChange]);

  // Animate region opacity transitions
  useEffect(() => {
    const targetOpacities: { [key: string]: number } = {};
    
    // Selecionar o GeoJSON apropriado
    const regionsGeoJSON = useCustomRegions && customRegionsGeoJSON 
      ? customRegionsGeoJSON 
      : RS_MESORREGIAO_GEOJSON;
    
    // Determine target opacities for each region
    regionsGeoJSON.features.forEach((feature: any) => {
      const regionName = feature.properties?.mesorregiao_nome;
      if (!regionName) return;
      
      const isHovered = hoveredRegion === regionName;
      const isSelected = selectedRegion === regionName;
      const hasSelectedRegion = selectedRegion !== null;
      const isOtherRegionSelected = hasSelectedRegion && !isSelected;
      
      if (isOtherRegionSelected) {
        targetOpacities[regionName] = 0.05;
      } else if (isSelected) {
        targetOpacities[regionName] = 0.45;
      } else if (isHovered) {
        targetOpacities[regionName] = 0.35;
      } else {
        targetOpacities[regionName] = 0.15;
      }
    });
    
    // Animate from current to target opacities
    const startOpacities = { ...regionOpacities };
    const startTime = Date.now();
    const duration = 200; // milliseconds for smooth transition
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-out function for smoother deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      
      const newOpacities: { [key: string]: number } = {};
      Object.keys(targetOpacities).forEach((regionName) => {
        const start = startOpacities[regionName] ?? 0.15;
        const target = targetOpacities[regionName];
        newOpacities[regionName] = start + (target - start) * eased;
      });
      
      setRegionOpacities(newOpacities);
      
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredRegion, selectedRegion, useCustomRegions, customRegionsGeoJSON]);

  // Fetch team members
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!session?.access_token) return;

      try {
        const response = await apiFetch(`${API_BASE_URL}/api/v1/usuarios/grupo`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          setTeamMembers(result.data || []);
        }
      } catch (error) {
        console.error('Erro ao buscar membros da equipe:', error);
      }
    };

    fetchTeamMembers();
  }, [session]);

  // Handle triggered city opening from parent (e.g., from list view)
  useEffect(() => {
    if (triggerCityId) {
      const city = (citiesWithCoordinates as any[]).find((c: any) => c.id === triggerCityId);
      if (city) {
        handleCitySelect(city);
      }
    }
  }, [triggerCityId]);

  // Fetch city data with real responsible
  const fetchCityData = useCallback(async (cityName: string, cityId: number, population: number, regionName: string): Promise<CityDetails> => {
    // Generate mock past campaigns for now (could be fetched from backend later)
    const pastCampaigns: PastCampaign[] = [];
    const years = [2006, 2010, 2014, 2018, 2022];
    const numCampaigns = 3 + Math.floor(Math.random() * 3);
    
    let baseVotes = Math.floor(population * (0.25 + Math.random() * 0.15));
    
    for (let i = 0; i < numCampaigns; i++) {
      pastCampaigns.push({
        year: years[i],
        votes: baseVotes
      });
      baseVotes = Math.floor(baseVotes * (1.05 + Math.random() * 0.15));
    }
    
    const lastCampaignVotes = pastCampaigns[pastCampaigns.length - 1].votes;
    const projectedVotes = Math.floor(lastCampaignVotes * (1.05 + Math.random() * 0.1));
    
    // Fetch real responsible from backend
    let responsible = null;
    let responsibleId = null;
    let responsibleEmail = null;
    
    if (session?.access_token) {
      try {
        const response = await apiFetch(`${API_BASE_URL}/api/v1/cidades/${cityId}/responsavel`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.data) {
            responsible = result.data.nome;
            responsibleId = result.data.id;
            responsibleEmail = result.data.email;
          }
        }
      } catch (error) {
        console.error('Erro ao buscar responsável:', error);
      }
    }
    
    return {
      name: cityName,
      cityId,
      population,
      pastCampaigns,
      projectedVotes,
      responsible,
      responsibleId,
      responsibleEmail,
      region: regionName
    };
  }, [session]);

  // Helper function to calculate bounds of a region's geometry
  const calculateRegionBounds = useCallback((geometry: any): Bounds => {
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;
    
    const processCoordinates = (coords: [number, number][]) => {
      coords.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });
    };
    
    if (geometry.type === 'Polygon') {
      geometry.coordinates.forEach((ring: [number, number][]) => {
        processCoordinates(ring);
      });
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach((polygon: [number, number][][]) => {
        polygon.forEach((ring: [number, number][]) => {
          processCoordinates(ring);
        });
      });
    }
    
    return {
      north: maxLat,
      south: minLat,
      east: maxLng,
      west: minLng
    };
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    // Intelligent search: search by city name
    const normalizedQuery = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const filtered = citiesWithCoordinates.filter(city => {
      const normalizedCityName = city.nome.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return normalizedCityName.includes(normalizedQuery);
    }).slice(0, 10); // Limit to 10 results
    
    setSearchResults(filtered);
    setShowSearchResults(true);
  }, []);

  // Handle city selection from search
  const handleCitySelect = useCallback(async (city: typeof citiesWithCoordinates[0]) => {
    setSearchQuery(city.nome);
    setShowSearchResults(false);
    
    // Mark city as selected on the map
    setSelectedCityName(city.nome);
    
    // Trigger the same modal behavior as clicking on the map
    // Pass the full city data as properties, matching the format from cityMarkers
    if (onCityClick) {
      const properties = {
        id: city.id,
        name: city.nome,
        population: city.population || 0,
        mesorregiao: city.microrregiao?.mesorregiao?.nome || 'Região desconhecida',
        uf: 'RS'
      };
      onCityClick(city.nome, properties);
    }
    
    // Zoom to the city's region
    const regionName = city.microrregiao?.mesorregiao?.nome;
    if (regionName) {
      zoomToRegion(regionName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCityClick]);

  // Animate zoom and pan to a region
  const zoomToRegion = useCallback((regionName: string) => {
    const regionsGeoJSON = useCustomRegions && customRegionsGeoJSON 
      ? customRegionsGeoJSON 
      : RS_MESORREGIAO_GEOJSON;
    
    const regionFeature = regionsGeoJSON.features.find(
      (f: any) => f.properties?.mesorregiao_nome === regionName || f.properties?.nome === regionName
    );
    
    if (!regionFeature || isAnimating) return;
    
    // Mark region as selected
    setSelectedRegion(regionName);
    
    const regionBounds = calculateRegionBounds(regionFeature.geometry);
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    // Calculate the zoom level needed to fit the region
    const regionWidth = regionBounds.east - regionBounds.west;
    const regionHeight = regionBounds.north - regionBounds.south;
    const stateWidth = bounds.east - bounds.west;
    const stateHeight = bounds.north - bounds.south;
    
    const zoomX = (stateWidth / regionWidth) * 0.85; // 85% to add padding
    const zoomY = (stateHeight / regionHeight) * 0.85;
    const targetZoom = Math.min(zoomX, zoomY, 4); // Max zoom of 4x
    
    // Calculate the center of the region in geographic coordinates
    const regionCenterLng = (regionBounds.east + regionBounds.west) / 2;
    const regionCenterLat = (regionBounds.north + regionBounds.south) / 2;
    
    // Convert region center to canvas coordinates at zoom level 1 with no pan
    const padding = 40;
    const effectiveWidth = width - padding * 2;
    const effectiveHeight = height - padding * 2;
    
    const lngRange = bounds.east - bounds.west;
    const latRange = bounds.north - bounds.south;
    
    const geoAspect = lngRange / latRange;
    const canvasAspect = effectiveWidth / effectiveHeight;
    
    let scale: number;
    let offsetX = padding;
    let offsetY = padding;
    
    if (geoAspect > canvasAspect) {
      scale = effectiveWidth / lngRange;
      offsetY = padding + (effectiveHeight - latRange * scale) / 2;
    } else {
      scale = effectiveHeight / latRange;
      offsetX = padding + (effectiveWidth - lngRange * scale) / 2;
    }
    
    // Position of region center at zoom=1, pan=0
    const baseRegionCenterX = offsetX + (regionCenterLng - bounds.west) * scale;
    const baseRegionCenterY = offsetY + (bounds.north - regionCenterLat) * scale;
    
    // Canvas center
    const canvasCenterX = width / 2;
    const canvasCenterY = height / 2;
    
    // Calculate pan offset needed to center the region
    // At targetZoom: regionPos = canvasCenter + (baseRegionPos - canvasCenter) * zoom + pan
    // We want: regionPos = canvasCenter
    // So: canvasCenter = canvasCenter + (baseRegionPos - canvasCenter) * zoom + pan
    // Therefore: pan = -(baseRegionPos - canvasCenter) * zoom
    const targetPanX = -(baseRegionCenterX - canvasCenterX) * targetZoom;
    const targetPanY = -(baseRegionCenterY - canvasCenterY) * targetZoom;
    
    // Animate the transition
    setIsAnimating(true);
    const startZoom = zoom;
    const startPanX = panOffset.x;
    const startPanY = panOffset.y;
    const duration = 800; // milliseconds
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-in-out function
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      const currentZoom = startZoom + (targetZoom - startZoom) * eased;
      const currentPanX = startPanX + (targetPanX - startPanX) * eased;
      const currentPanY = startPanY + (targetPanY - startPanY) * eased;
      
      setZoom(currentZoom);
      setPanOffset({ x: currentPanX, y: currentPanY });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };
    
    animate();
  }, [bounds, zoom, panOffset, calculateRegionBounds, isAnimating, useCustomRegions, customRegionsGeoJSON]);

  const geoToCanvas = useCallback((
    lng: number,
    lat: number,
    canvasWidth: number,
    canvasHeight: number,
    padding: number = 40
  ) => {
    // Add extra padding on the left to shift map to the right
    // This prevents overlap with the business indicators panel
    const leftPadding = padding + 200; // Extra 200px on left
    const rightPadding = padding;
    const topPadding = padding;
    const bottomPadding = padding;
    
    const effectiveWidth = canvasWidth - leftPadding - rightPadding;
    const effectiveHeight = canvasHeight - topPadding - bottomPadding;
    
    const lngRange = bounds.east - bounds.west;
    const latRange = bounds.north - bounds.south;
    
    const geoAspect = lngRange / latRange;
    const canvasAspect = effectiveWidth / effectiveHeight;
    
    let scale: number;
    let offsetX = leftPadding;
    let offsetY = topPadding;
    
    if (geoAspect > canvasAspect) {
      scale = effectiveWidth / lngRange;
      offsetY = topPadding + (effectiveHeight - latRange * scale) / 2;
    } else {
      scale = effectiveHeight / latRange;
      offsetX = leftPadding + (effectiveWidth - lngRange * scale) / 2;
    }
    
    // Apply zoom and pan transformations
    // Adjust center to account for asymmetric padding
    const centerX = (canvasWidth + leftPadding - rightPadding) / 2;
    const centerY = canvasHeight / 2;
    
    let x = offsetX + (lng - bounds.west) * scale;
    let y = offsetY + (bounds.north - lat) * scale;
    
    // Apply zoom around adjusted center
    x = centerX + (x - centerX) * zoom + panOffset.x;
    y = centerY + (y - centerY) * zoom + panOffset.y;
    
    return { x, y };
  }, [bounds, zoom, panOffset]);

  // Check if a point is inside a polygon using ray casting algorithm
  const isPointInPolygon = useCallback((x: number, y: number, polygonPoints: {x: number, y: number}[]) => {
    let inside = false;
    for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
      const xi = polygonPoints[i].x, yi = polygonPoints[i].y;
      const xj = polygonPoints[j].x, yj = polygonPoints[j].y;
      
      const intersect = ((yi > y) !== (yj > y))
          && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }, []);

  // Check if a city belongs to the selected region
  const cityBelongsToSelectedRegion = useCallback((cityProperties: any, selectedRegionName: string): boolean => {
    if (useCustomRegions && customRegionsGeoJSON) {
      // Para regiões customizadas, verificar se a cidade está na lista de cidade_ids
      const regionFeature = customRegionsGeoJSON.features.find(
        (f: any) => f.properties?.mesorregiao_nome === selectedRegionName
      );
      
      if (regionFeature?.properties?.cidade_ids) {
        // Pegar o ID IBGE da cidade
        const cityId = cityProperties?.id || cityProperties?.ibge_id;
        
        // Verificar se está nas cidades da região
        return regionFeature.properties.cidade_ids.includes(cityId);
      }
    }
    
    // Para regiões tradicionais, comparar pelo nome da mesorregião
    return cityProperties?.mesorregiao === selectedRegionName;
  }, [useCustomRegions, customRegionsGeoJSON]);

  // Get region at mouse position
  const getRegionAtPoint = useCallback((x: number, y: number, width: number, height: number): string | null => {
    // Always check state border first — Voronoi extends infinitely so without this
    // hovering outside the map still triggers a region.
    const stateFeature = RS_GEOJSON.features[0];
    if (stateFeature?.geometry.type === 'Polygon') {
      const statePoints = (stateFeature.geometry.coordinates[0] as [number, number][])
        .map(([lng, lat]) => geoToCanvas(lng, lat, width, height));
      if (!isPointInPolygon(x, y, statePoints)) return null;
    }

    if (useCustomRegions && customRegionsGeoJSON) {
      // Voronoi nearest-city lookup for custom regions
      let minDist = Infinity;
      let nearestRegion: string | null = null;

      customRegionsGeoJSON.features.forEach((feature: any) => {
        const cityIds: number[] = feature.properties?.cidade_ids || [];
        const regionName: string = feature.properties?.mesorregiao_nome || '';
        if (!regionName) return;

        cityIds.forEach((cityId: number) => {
          const city = (citiesWithCoordinates as any[]).find((c: any) => c.id === cityId);
          if (city?.latitude && city?.longitude) {
            const { x: cx, y: cy } = geoToCanvas(city.longitude, city.latitude, width, height);
            const dx = x - cx;
            const dy = y - cy;
            const d = dx * dx + dy * dy;
            if (d < minDist) { minDist = d; nearestRegion = regionName; }
          }
        });
      });

      return nearestRegion;
    }

    // Traditional polygon hit-test for mesorregião
    for (const feature of RS_MESORREGIAO_GEOJSON.features) {
      const regionName = feature.properties?.mesorregiao_nome;
      if (!regionName) continue;
      
      const checkPolygonRing = (coordinates: [number, number][]): boolean => {
        const points = coordinates.map(([lng, lat]) => geoToCanvas(lng, lat, width, height));
        return isPointInPolygon(x, y, points);
      };
      
      if (feature.geometry.type === 'Polygon') {
        if (checkPolygonRing(feature.geometry.coordinates[0] as [number, number][])) {
          return regionName;
        }
      } else if (feature.geometry.type === 'MultiPolygon') {
        for (const polygon of feature.geometry.coordinates) {
          if (checkPolygonRing(polygon[0] as [number, number][])) {
            return regionName;
          }
        }
      }
    }
    return null;
  }, [geoToCanvas, isPointInPolygon, useCustomRegions, customRegionsGeoJSON]);

  const drawMap = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Create clipping path for state border
    const stateFeature = RS_GEOJSON.features[0];
    let statePath: Path2D | null = null;
    
    if (stateFeature && stateFeature.geometry.type === 'Polygon') {
      const coordinates = stateFeature.geometry.coordinates[0];
      
      // Create state border path
      statePath = new Path2D();
      coordinates.forEach((coord, index) => {
        const [lng, lat] = coord as [number, number];
        const { x, y } = geoToCanvas(lng, lat, width, height);
        
        if (index === 0) {
          statePath!.moveTo(x, y);
        } else {
          statePath!.lineTo(x, y);
        }
      });
      statePath.closePath();
      
      // Don't fill state background - only regions will have background colors
    }

    // Draw regions with different colors using actual GeoJSON data (clipped to state border)
    if (showCities && statePath) {
      // Save context state
      ctx.save();
      
      // Set clipping region to state border
      ctx.clip(statePath);

      if (useCustomRegions && customRegionsGeoJSON) {
        // ── VORONOI RENDERING FOR CUSTOM REGIONS (cached) ─────────────────────
        // Phase 1 (EXPENSIVE, cached): build regionMap + graph-color adjacency
        // Phase 2 (FAST, per-frame): fill regions + draw crisp border lines
        const SCALE = 5;
        const ofw = Math.ceil(width / SCALE);
        const ofh = Math.ceil(height / SCALE);
        const cacheKey = `${ofw}x${ofh}|${zoom.toFixed(3)}|${panOffset.x.toFixed(1)},${panOffset.y.toFixed(1)}|${customRegionsGeoJSON.features.length}`;

        let cache = voronoiCacheRef.current;
        if (!cache || cache.key !== cacheKey) {
          // ── Build regionMeta (name only; colorIndex assigned after graph coloring) ──
          const regionMeta: { regionName: string; colorIndex: number }[] = [];
          const vx: Float32Array = new Float32Array(10000);
          const vy: Float32Array = new Float32Array(10000);
          const vm: Int16Array   = new Int16Array(10000);
          let vCount = 0;

          customRegionsGeoJSON.features.forEach((feature: any) => {
            const cityIds: number[] = feature.properties?.cidade_ids || [];
            const regionName: string = feature.properties?.mesorregiao_nome || '';
            if (!regionName) return;
            const metaIdx = regionMeta.length;
            regionMeta.push({ regionName, colorIndex: 0 }); // colorIndex set below
            cityIds.forEach((cityId: number) => {
              const city = (citiesWithCoordinates as any[]).find((c: any) => c.id === cityId);
              if (city?.latitude && city?.longitude) {
                const { x, y } = geoToCanvas(city.longitude, city.latitude, width, height);
                if (vCount < 10000) {
                  vx[vCount] = x / SCALE;
                  vy[vCount] = y / SCALE;
                  vm[vCount] = metaIdx;
                  vCount++;
                }
              }
            });
          });

          // ── Voronoi pixel assignment ──────────────────────────────────────────
          const regionMap = new Int16Array(ofw * ofh).fill(-1);
          for (let py = 0; py < ofh; py++) {
            for (let px = 0; px < ofw; px++) {
              let minDist = Infinity;
              let nearest = -1;
              for (let i = 0; i < vCount; i++) {
                const dx = px - vx[i];
                const dy = py - vy[i];
                const d = dx * dx + dy * dy;
                if (d < minDist) { minDist = d; nearest = vm[i]; }
              }
              regionMap[py * ofw + px] = nearest;
            }
          }

          // ── Build adjacency graph from regionMap ──────────────────────────────
          const n = regionMeta.length;
          const adj: Set<number>[] = Array.from({ length: n }, () => new Set<number>());
          for (let py = 0; py < ofh - 1; py++) {
            for (let px = 0; px < ofw - 1; px++) {
              const a = regionMap[py * ofw + px];
              const b = regionMap[py * ofw + px + 1];   // right neighbor
              const c = regionMap[(py + 1) * ofw + px]; // bottom neighbor
              if (a >= 0 && b >= 0 && a !== b) { adj[a].add(b); adj[b].add(a); }
              if (a >= 0 && c >= 0 && a !== c) { adj[a].add(c); adj[c].add(a); }
            }
          }

          // ── Greedy graph coloring (ensures neighbors get different colors) ────
          const numColors = CUSTOM_REGION_COLORS.length;
          const assigned = new Int8Array(n).fill(-1);
          for (let i = 0; i < n; i++) {
            const usedByNeighbors = new Set<number>();
            adj[i].forEach(nb => { if (assigned[nb] >= 0) usedByNeighbors.add(assigned[nb]); });
            for (let c = 0; c < numColors; c++) {
              if (!usedByNeighbors.has(c)) { assigned[i] = c; break; }
            }
            if (assigned[i] < 0) assigned[i] = i % numColors; // fallback
            regionMeta[i].colorIndex = assigned[i];
          }

          cache = { key: cacheKey, regionMap, regionMeta, ofw, ofh };
          voronoiCacheRef.current = cache;
        }

        // ── Phase 2: fast recolor pass ────────────────────────────────────────
        const { regionMap, regionMeta } = cache;
        const parsedColors = CUSTOM_REGION_COLORS.map(c => {
          const m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          return m ? [parseInt(m[1]), parseInt(m[2]), parseInt(m[3])] : [128, 128, 128];
        });

        const offCanvas = document.createElement('canvas');
        offCanvas.width = ofw;
        offCanvas.height = ofh;
        const offCtx = offCanvas.getContext('2d')!;
        const imageData = offCtx.createImageData(ofw, ofh);
        const data = imageData.data;
        const hasSel = selectedRegion !== null;

        for (let i = 0; i < regionMap.length; i++) {
          const metaIdx = regionMap[i];
          if (metaIdx < 0) continue;
          const meta = regionMeta[metaIdx];
          const isSel = selectedRegion === meta.regionName;
          const isHov = hoveredRegion === meta.regionName;
          const alpha = hasSel && !isSel ? 13 : isSel ? 115 : isHov ? 89 : 38;
          const rgb = parsedColors[meta.colorIndex];
          const idx = i * 4;
          data[idx] = rgb[0]; data[idx+1] = rgb[1]; data[idx+2] = rgb[2]; data[idx+3] = alpha;
        }

        offCtx.putImageData(imageData, 0, 0);
        ctx.imageSmoothingEnabled = false; // keep fill pixels sharp before border pass
        ctx.drawImage(offCanvas, 0, 0, width, height);

        // ── Phase 3: crisp border lines at full canvas resolution ─────────────
        // Walk the low-res regionMap edges and stroke them at SCALE width on ctx
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
        ctx.lineWidth = Math.max(1, zoom * 1.5);
        ctx.lineCap = 'round';
        ctx.beginPath();
        for (let py = 0; py < ofh - 1; py++) {
          for (let px = 0; px < ofw - 1; px++) {
            const here  = regionMap[py * ofw + px];
            const right = regionMap[py * ofw + px + 1];
            const below = regionMap[(py + 1) * ofw + px];
            // Horizontal edge (border between here and below)
            if (here !== below) {
              const fy = (py + 1) * SCALE;
              const fx0 = px * SCALE;
              const fx1 = (px + 1) * SCALE;
              ctx.moveTo(fx0, fy);
              ctx.lineTo(fx1, fy);
            }
            // Vertical edge (border between here and right)
            if (here !== right) {
              const fx = (px + 1) * SCALE;
              const fy0 = py * SCALE;
              const fy1 = (py + 1) * SCALE;
              ctx.moveTo(fx, fy0);
              ctx.lineTo(fx, fy1);
            }
          }
        }
        ctx.stroke();
        ctx.restore();
      } else {
        // ── TRADITIONAL MESORREGIÃO RENDERING (polygon-based) ─────────────────
        RS_MESORREGIAO_GEOJSON.features.forEach((feature: any) => {
          const regionName = feature.properties?.mesorregiao_nome;
          if (!regionName) return;

          const isHovered = hoveredRegion === regionName;
          const isSelected = selectedRegion === regionName;
          const hasSelectedRegion = selectedRegion !== null;
          const isOtherRegionSelected = hasSelectedRegion && !isSelected;

          const baseColor = REGION_COLORS[regionName] || 'rgba(156, 163, 175, 0.15)';
          const baseBorderColor = REGION_BORDER_COLORS[regionName] || 'rgba(156, 163, 175, 0.15)';

          const currentOpacity = regionOpacities[regionName] ?? 0.15;
          const regionColor = baseColor.replace('0.15', currentOpacity.toFixed(2));
          const regionBorderColor = baseBorderColor.replace('0.15', currentOpacity.toFixed(2));

          const drawPolygonRing = (coordinates: [number, number][]) => {
            if (coordinates.length === 0) return;
            const firstPoint = geoToCanvas(coordinates[0][0], coordinates[0][1], width, height);
            ctx.moveTo(firstPoint.x, firstPoint.y);
            for (let i = 1; i < coordinates.length; i++) {
              const point = geoToCanvas(coordinates[i][0], coordinates[i][1], width, height);
              ctx.lineTo(point.x, point.y);
            }
          };

          ctx.beginPath();
          if (feature.geometry.type === 'Polygon') {
            const coordinates = feature.geometry.coordinates as [number, number][][];
            drawPolygonRing(coordinates[0]);
            for (let i = 1; i < coordinates.length; i++) drawPolygonRing(coordinates[i]);
          } else if (feature.geometry.type === 'MultiPolygon') {
            const coordinates = feature.geometry.coordinates as [number, number][][][];
            coordinates.forEach(polygon => {
              drawPolygonRing(polygon[0]);
              for (let i = 1; i < polygon.length; i++) drawPolygonRing(polygon[i]);
            });
          }
          ctx.closePath();

          ctx.fillStyle = regionColor;
          ctx.fill();

          if (!isOtherRegionSelected) {
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
            ctx.lineWidth = isSelected ? 6 : (isHovered ? 5 : 4);
            ctx.stroke();
          }

          ctx.strokeStyle = regionBorderColor;
          ctx.lineWidth = isSelected ? 4 : (isHovered ? 3.5 : 2.5);
          ctx.stroke();

          if (isSelected || isHovered) {
            ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
            ctx.shadowBlur = isSelected ? 15 : 10;
            ctx.fill();
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
          }
        });
      }
      
      // Restore context state (remove clipping)
      ctx.restore();
    }
    
    // State border removed since regions now cover it

    if (showCities) {
      const markers: CityMarker[] = [];
      
      // Sort cities by population to draw smaller cities first, larger cities on top
      const sortedCities = [...citiesWithCoordinates].sort((a, b) => 
        (a.population || 0) - (b.population || 0)
      );
      
      sortedCities.forEach((city) => {
        if (city.latitude && city.longitude) {
          const { x, y } = geoToCanvas(city.longitude, city.latitude, width, height);
          const name = city.nome;
          const isHovered = hoveredCity === name;
          const isSelected = selectedCityName === name;
          const population = city.population || 0;
          const cityRegion = city.microrregiao?.mesorregiao?.nome;
          
          // Check if this city belongs to a non-selected region.
          // In custom regions mode, the region names don't match mesorregião names,
          // so we check membership via cidade_ids from the GeoJSON properties instead.
          let isCityInOtherRegion: boolean | null | string = false;
          if (selectedRegion) {
            if (useCustomRegions && customRegionsGeoJSON) {
              const selectedFeature = customRegionsGeoJSON.features.find(
                (f: any) => f.properties?.mesorregiao_nome === selectedRegion
              );
              const cityIds: number[] = selectedFeature?.properties?.cidade_ids ?? [];
              isCityInOtherRegion = cityIds.length > 0 && !cityIds.includes(city.id);
            } else {
              isCityInOtherRegion = cityRegion !== selectedRegion;
            }
          }
          
          markers.push({
            name,
            x,
            y,
            properties: {
              name: city.nome,
              id: city.id,
              microrregiao: city.microrregiao?.nome,
              mesorregiao: city.microrregiao?.mesorregiao?.nome,
              population: population
            }
          });
          
          // Uniform size for all cities
          const baseRadius = 5;
          const radius = isSelected ? baseRadius + 3 : (isHovered ? baseRadius + 2 : baseRadius);
          
          // Draw glow effect for selected or hovered city (only if not in other region)
          if ((isSelected || isHovered) && !isCityInOtherRegion) {
            ctx.beginPath();
            ctx.arc(x, y, radius + 6, 0, Math.PI * 2);
            const glowColor = isSelected ? 'rgba(239, 68, 68, 0.4)' : 'rgba(37, 99, 235, 0.3)';
            ctx.fillStyle = glowColor;
            ctx.fill();
          }
          
          // Draw city marker
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          
          // Uniform color for all cities with selection/hover states
          let fillColor = cityColor;
          if (isCityInOtherRegion) {
            // Cities in other regions are very transparent
            fillColor = 'rgba(148, 163, 184, 0.3)'; // Very faded gray
          } else if (isSelected) {
            fillColor = '#ef4444'; // Red for selected
          } else if (isHovered) {
            fillColor = '#1d4ed8'; // Blue for hovered
          }
          
          ctx.fillStyle = fillColor;
          ctx.fill();
          
          // White border for better visibility (but transparent for other regions)
          if (isCityInOtherRegion) {
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
            ctx.lineWidth = 1;
          } else {
            ctx.strokeStyle = 'white';
            ctx.lineWidth = 2;
          }
          ctx.stroke();
          
          // Add shadow for selected city
          if (isSelected) {
            ctx.shadowColor = 'rgba(239, 68, 68, 0.4)';
            ctx.shadowBlur = 6;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
          }
          
          // Reset shadow
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          
          // Draw city name for selected city
          if (isSelected) {
            ctx.font = '600 12px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            
            // Draw text background
            const textMetrics = ctx.measureText(name);
            const textWidth = textMetrics.width;
            const textHeight = 16;
            const padding = 6;
            
            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.beginPath();
            ctx.roundRect(
              x - textWidth / 2 - padding,
              y + radius + 8,
              textWidth + padding * 2,
              textHeight + padding,
              4
            );
            ctx.fill();
            
            // Draw text shadow
            ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
            ctx.shadowBlur = 4;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 2;
            ctx.fill();
            
            // Reset shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            
            // Draw border around text background
            ctx.strokeStyle = '#e2e8f0';
            ctx.lineWidth = 1;
            ctx.stroke();
            
            // Draw text
            ctx.fillStyle = '#1e293b';
            ctx.fillText(name, x, y + radius + 12);
          }
        }
      });
      
      setCityMarkers(markers);
    }
  }, [showCities, backgroundColor, cityColor, geoToCanvas, hoveredCity, hoveredRegion, selectedCityName, selectedRegion, regionOpacities, useCustomRegions, customRegionsGeoJSON]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check for city hover first (cities have priority) - but ONLY if a region is selected
    let foundCity: CityMarker | null = null;
    if (selectedRegion) {
      for (const marker of cityMarkers) {
        const distance = Math.sqrt(Math.pow(x - marker.x, 2) + Math.pow(y - marker.y, 2));
        if (distance <= 15) {
          // Only allow hover if city is in the selected region
          if (cityBelongsToSelectedRegion(marker.properties, selectedRegion)) {
            foundCity = marker;
            break;
          }
        }
      }
    }

    if (foundCity) {
      canvas.style.cursor = 'pointer';
      setHoveredCity(foundCity.name);
      // Also hover the region that contains this city
      const cityRegion = foundCity.properties?.mesorregiao;
      setHoveredRegion(cityRegion || null);
      setTooltip({
        visible: true,
        x: foundCity.x,
        y: foundCity.y,
        name: foundCity.name,
        population: foundCity.properties?.population || 0
      });
      setRegionTooltip({ visible: false, x: 0, y: 0, name: '' });
    } else {
      // Check for region hover - only if no region is selected
      if (!selectedRegion) {
        const hoveredRegionName = getRegionAtPoint(x, y, rect.width, rect.height);
        
        if (hoveredRegionName) {
          canvas.style.cursor = 'pointer';
          setHoveredCity(null);
          setHoveredRegion(hoveredRegionName);
          setTooltip({ visible: false, x: 0, y: 0, name: '', population: 0 });
          setRegionTooltip({
            visible: true,
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            name: hoveredRegionName
          });
        } else {
          canvas.style.cursor = 'default';
          setHoveredCity(null);
          setHoveredRegion(null);
          setTooltip({ visible: false, x: 0, y: 0, name: '', population: 0 });
          setRegionTooltip({ visible: false, x: 0, y: 0, name: '' });
        }
      } else {
        // If a region is selected, only allow cursor default (cities will override this)
        canvas.style.cursor = 'default';
        setHoveredCity(null);
        setHoveredRegion(null);
        setTooltip({ visible: false, x: 0, y: 0, name: '', population: 0 });
        setRegionTooltip({ visible: false, x: 0, y: 0, name: '' });
      }
    }
  }, [cityMarkers, getRegionAtPoint, selectedRegion, cityBelongsToSelectedRegion]);

  const handleClick = useCallback(async (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check for city click first - but ONLY if a region is selected
    if (selectedRegion) {
      for (const marker of cityMarkers) {
        const distance = Math.sqrt(Math.pow(x - marker.x, 2) + Math.pow(y - marker.y, 2));
        if (distance <= 15) {
          // Only allow click if city is in the selected region
          if (cityBelongsToSelectedRegion(marker.properties, selectedRegion)) {
            // Mark city as selected
            setSelectedCityName(marker.name);

            if (onCityClick) {
              // Delegate to parent — no need to fetch internal side sheet data
              onCityClick(marker.name, marker.properties);
            } else {
              // Fetch real city data and open internal side sheet
              setIsLoadingResponsible(true);
              const cityData = await fetchCityData(
                marker.properties.name,
                marker.properties.id,
                marker.properties.population || 0,
                marker.properties.mesorregiao || 'Região desconhecida'
              );
              setIsLoadingResponsible(false);
              setSelectedCity(cityData);
              setIsSideSheetOpen(true);
            }
            return;
          }
        }
      }
    }

    // Check for region click
    const clickedRegion = getRegionAtPoint(x, y, rect.width, rect.height);
    
    if (selectedRegion) {
      // If a region is already selected
      if (clickedRegion && clickedRegion !== selectedRegion) {
        // Clicked on a different region - close current region view and restore zoom
        if (!isAnimating) {
          setSelectedRegion(null);
          // Also unselect city and close side sheet
          setSelectedCityName(null);
          setIsSideSheetOpen(false);
          setSelectedCity(null);
          
          setIsAnimating(true);
          const startZoom = zoom;
          const startPanX = panOffset.x;
          const startPanY = panOffset.y;
          const duration = 600;
          const startTime = Date.now();
          
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            const eased = progress < 0.5
              ? 2 * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            
            const currentZoom = startZoom + (1 - startZoom) * eased;
            const currentPanX = startPanX + (0 - startPanX) * eased;
            const currentPanY = startPanY + (0 - startPanY) * eased;
            
            setZoom(currentZoom);
            setPanOffset({ x: currentPanX, y: currentPanY });
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setIsAnimating(false);
            }
          };
          
          animate();
        }
      } else if (!clickedRegion) {
        // Clicked outside any region - close region view and restore zoom
        if (!isAnimating) {
          setSelectedRegion(null);
          // Also unselect city and close side sheet
          setSelectedCityName(null);
          setIsSideSheetOpen(false);
          setSelectedCity(null);
          
          setIsAnimating(true);
          const startZoom = zoom;
          const startPanX = panOffset.x;
          const startPanY = panOffset.y;
          const duration = 600;
          const startTime = Date.now();
          
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            const eased = progress < 0.5
              ? 2 * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            
            const currentZoom = startZoom + (1 - startZoom) * eased;
            const currentPanX = startPanX + (0 - startPanX) * eased;
            const currentPanY = startPanY + (0 - startPanY) * eased;
            
            setZoom(currentZoom);
            setPanOffset({ x: currentPanX, y: currentPanY });
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setIsAnimating(false);
            }
          };
          
          animate();
        }
      }
      // If clicked on the same selected region, do nothing
    } else {
      // No region is selected, allow selecting a new region
      if (clickedRegion) {
        zoomToRegion(clickedRegion);
      }
    }
  }, [cityMarkers, onCityClick, getRegionAtPoint, zoomToRegion, fetchCityData, selectedRegion, isAnimating, zoom, panOffset, cityBelongsToSelectedRegion]);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
    // Disable zoom if region is selected
    if (selectedRegion) return;
    
    e.preventDefault();
    // Less sensitive zoom: smaller delta changes
    const delta = e.deltaY > 0 ? 0.95 : 1.05;
    setZoom(prevZoom => Math.min(Math.max(prevZoom * delta, 0.5), 5));
  }, [selectedRegion]);

  const closeRegionView = useCallback(() => {
    if (isAnimating) return;
    
    // Clear selected region
    setSelectedRegion(null);
    // Also unselect city and close side sheet
    setSelectedCityName(null);
    setIsSideSheetOpen(false);
    setSelectedCity(null);
    
    setIsAnimating(true);
    const startZoom = zoom;
    const startPanX = panOffset.x;
    const startPanY = panOffset.y;
    const duration = 600;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      const currentZoom = startZoom + (1 - startZoom) * eased;
      const currentPanX = startPanX + (0 - startPanX) * eased;
      const currentPanY = startPanY + (0 - startPanY) * eased;
      
      setZoom(currentZoom);
      setPanOffset({ x: currentPanX, y: currentPanY });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };
    
    animate();
  }, [zoom, panOffset, isAnimating]);

  // Handlers for editing projection
  const handleEditProjection = useCallback(() => {
    if (selectedCity) {
      setIsEditingProjection(true);
      setEditedProjection(selectedCity.projectedVotes.toString());
    }
  }, [selectedCity]);

  const handleSaveProjection = useCallback(() => {
    if (selectedCity && editedProjection) {
      const newValue = parseInt(editedProjection.replace(/\D/g, ''), 10);
      if (!isNaN(newValue) && newValue > 0) {
        setSelectedCity({
          ...selectedCity,
          projectedVotes: newValue
        });
        setIsEditingProjection(false);
      }
    }
  }, [selectedCity, editedProjection]);

  const handleCancelEditProjection = useCallback(() => {
    setIsEditingProjection(false);
    setEditedProjection('');
  }, []);

  const handleResponsibleClick = useCallback(() => {
    // If admin and no responsible, show assign dropdown
    if (isAdmin && !selectedCity?.responsible) {
      setIsAssigningResponsible(true);
    } else if (selectedCity?.responsible) {
      // Navigate to Team page with the responsible person's name
      navigate('/time', { state: { memberName: selectedCity.responsible } });
    } else if (!isAdmin) {
      // Non-admin clicking on unassigned city - do nothing or show message
      return;
    }
  }, [selectedCity, navigate, isAdmin]);

  const handleAssignResponsible = useCallback(async (memberId: number) => {
    if (!selectedCity || !session?.access_token) return;

    setIsLoadingResponsible(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/cidades/${selectedCity.cityId}/responsavel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ responsavel_id: memberId })
      });

      if (response.ok) {
        const result = await response.json();
        // Update selected city with new responsible
        setSelectedCity({
          ...selectedCity,
          responsible: result.data.nome,
          responsibleId: result.data.id,
          responsibleEmail: result.data.email
        });
      }
    } catch (error) {
      console.error('Erro ao atribuir responsável:', error);
    } finally {
      setIsLoadingResponsible(false);
      setIsAssigningResponsible(false);
    }
  }, [selectedCity, session]);

  const resetZoom = useCallback(() => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    const startZoom = zoom;
    const startPanX = panOffset.x;
    const startPanY = panOffset.y;
    const duration = 600;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      const currentZoom = startZoom + (1 - startZoom) * eased;
      const currentPanX = startPanX + (0 - startPanX) * eased;
      const currentPanY = startPanY + (0 - startPanY) * eased;
      
      setZoom(currentZoom);
      setPanOffset({ x: currentPanX, y: currentPanY });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };
    
    animate();
  }, [zoom, panOffset, isAnimating]);

  useEffect(() => {
    drawMap();

    const handleResize = () => {
      drawMap();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawMap]);

  const formatPopulation = (pop: number) => {
    if (pop >= 1000000) {
      return `${(pop / 1000000).toFixed(1)}M habitantes`;
    }
    if (pop >= 1000) {
      return `${(pop / 1000).toFixed(0)}K habitantes`;
    }
    return `${pop} habitantes`;
  };

  const formatVotes = (votes: number) => {
    return votes.toLocaleString('pt-BR');
  };

  return (
    <MapContainer ref={containerRef}>
      <Canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        onWheel={handleWheel}
        onMouseLeave={() => {
          setHoveredCity(null);
          setHoveredRegion(null);
          setTooltip({ visible: false, x: 0, y: 0, name: '', population: 0 });
          setRegionTooltip({ visible: false, x: 0, y: 0, name: '' });
        }}
      />
      <CityTooltip
        $visible={tooltip.visible}
        $x={tooltip.x}
        $y={tooltip.y}
      >
        <h4>{tooltip.name}</h4>
        <p>{formatPopulation(tooltip.population)}</p>
      </CityTooltip>
      <RegionTooltip
        $visible={regionTooltip.visible}
        $x={regionTooltip.x}
        $y={regionTooltip.y}
      >
        {regionTooltip.name}
        <br />
        <small style={{ fontSize: '11px', opacity: 0.8 }}>Clique para expandir</small>
      </RegionTooltip>
      
      {/* Search Input */}
      <SearchContainer>
        <SearchInputWrapper>
          <SearchInput
            type="text"
            placeholder="Buscar cidade..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => {
              if (searchResults.length > 0) {
                setShowSearchResults(true);
              }
            }}
            onBlur={() => {
              // Delay to allow click on results
              setTimeout(() => setShowSearchResults(false), 200);
            }}
          />
          <SearchIcon>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
          </SearchIcon>
        </SearchInputWrapper>
        <SearchResults $visible={showSearchResults}>
          {searchResults.length > 0 ? (
            searchResults.map((city) => (
              <SearchResultItem
                key={city.id}
                onClick={() => handleCitySelect(city)}
              >
                <div className="city-name">{city.nome}</div>
                <div className="city-info">
                  {city.microrregiao?.mesorregiao?.nome} • {formatPopulation(city.population || 0)}
                </div>
              </SearchResultItem>
            ))
          ) : (
            searchQuery.trim() !== '' && <NoResults>Nenhuma cidade encontrada</NoResults>
          )}
        </SearchResults>
      </SearchContainer>
      
      <CloseRegionButton 
        $visible={selectedRegion !== null}
        onClick={closeRegionView}
      >
        <span className="icon">✕</span>
        <span>Fechar {selectedRegion}</span>
      </CloseRegionButton>
      
      <ResetButton 
        $visible={!selectedRegion && (zoom !== 1 || panOffset.x !== 0 || panOffset.y !== 0)}
        onClick={resetZoom}
      >
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
          <path d="M21 3v5h-5" />
        </svg>
      </ResetButton>
      
      {/* City Details Side Sheet - Rendered via Portal */}
      {ReactDOM.createPortal(
        <SideSheet $isOpen={isSideSheetOpen}>
          {selectedCity && (
            <>
              <SideSheetHeader>
                <div>
                  <CityTitle>{selectedCity.name}</CityTitle>
                  <CitySubtitle>{selectedCity.region}</CitySubtitle>
                </div>
                <CloseButton onClick={() => {
                  setIsSideSheetOpen(false);
                setSelectedCityName(null);
              }}>
                ×
              </CloseButton>
            </SideSheetHeader>
            
            <SideSheetContent>
              {/* Population */}
              <InfoSection>
                <InfoLabel>População</InfoLabel>
                <InfoValue>{formatPopulation(selectedCity.population)}</InfoValue>
              </InfoSection>
              
              {/* Past Campaigns */}
              {canViewElectionResults && (
              <InfoSection>
                <InfoLabel>Campanhas Anteriores</InfoLabel>
                <InfoCard>
                  {selectedCity.pastCampaigns.map((campaign, index) => (
                    <div 
                      key={campaign.year} 
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: index < selectedCity.pastCampaigns.length - 1 ? '1px solid #e2e8f0' : 'none'
                      }}
                    >
                      <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '600' }}>
                        {campaign.year}
                      </span>
                      <span style={{ fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>
                        {formatVotes(campaign.votes)} votos
                      </span>
                    </div>
                  ))}
                </InfoCard>
              </InfoSection>
              )}
              
              {/* Projection - Editable */}
              {canViewElectionResults && (
              <InfoSection>
                <InfoLabel>Projeção 2026</InfoLabel>
                <StatCard style={{ background: 'var(--accent)' }}>
                  {isEditingProjection ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={editedProjection}
                        onChange={(e) => setEditedProjection(e.target.value)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          fontSize: '18px',
                          fontWeight: '700',
                          border: '2px solid white',
                          borderRadius: '6px',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#1e293b'
                        }}
                        autoFocus
                      />
                      <button
                        onClick={handleSaveProjection}
                        style={{
                          padding: '8px 16px',
                          background: 'white',
                          color: 'var(--accent)',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Salvar
                      </button>
                      <button
                        onClick={handleCancelEditProjection}
                        style={{
                          padding: '8px 16px',
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={handleEditProjection}
                      style={{ 
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <StatValue style={{ color: 'white' }}>{formatVotes(selectedCity.projectedVotes)}</StatValue>
                      <svg 
                        fill="none" 
                        stroke="white" 
                        strokeWidth="2" 
                        viewBox="0 0 24 24"
                        style={{ width: '20px', height: '20px', opacity: 0.7 }}
                      >
                        <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                  )}
                </StatCard>
              </InfoSection>
              )}
              
              {/* Comparison */}
              {canViewElectionResults && selectedCity.pastCampaigns.length > 0 && (
              <InfoSection>
                <InfoLabel>Crescimento Projetado</InfoLabel>
                <InfoCard>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      vs. {selectedCity.pastCampaigns[selectedCity.pastCampaigns.length - 1].year}
                    </span>
                    <span style={{ 
                      fontSize: '18px', 
                      fontWeight: '700', 
                      color: '#10b981'
                    }}>
                      +{Math.round(((selectedCity.projectedVotes - selectedCity.pastCampaigns[selectedCity.pastCampaigns.length - 1].votes) / selectedCity.pastCampaigns[selectedCity.pastCampaigns.length - 1].votes) * 100)}%
                    </span>
                  </div>
                </InfoCard>
              </InfoSection>
              )}
              
              {/* Responsible Person */}
              <InfoSection>
                <InfoLabel>Responsável pela Cidade</InfoLabel>
                {isLoadingResponsible ? (
                  <ResponsibleCard style={{ cursor: 'default', opacity: 0.6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '50%',
                        border: '3px solid var(--accent)',
                        borderTopColor: 'transparent',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <span style={{ fontSize: '14px', color: '#64748b' }}>Carregando...</span>
                    </div>
                  </ResponsibleCard>
                ) : isAssigningResponsible ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {teamMembers.map(member => (
                      <ResponsibleCard 
                        key={member.id}
                        onClick={() => handleAssignResponsible(member.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Avatar>
                          {member.nome.split(' ').map((n: string) => n[0]).join('')}
                        </Avatar>
                        <ResponsibleInfo>
                          <ResponsibleName>{member.nome}</ResponsibleName>
                          <ResponsibleRole>{member.email}</ResponsibleRole>
                        </ResponsibleInfo>
                      </ResponsibleCard>
                    ))}
                    <button
                      onClick={() => setIsAssigningResponsible(false)}
                      style={{
                        padding: '8px',
                        background: '#f1f5f9',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#64748b'
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <ResponsibleCard onClick={handleResponsibleClick}>
                    <Avatar>
                      {selectedCity.responsible ? selectedCity.responsible.split(' ').map(n => n[0]).join('') : '?'}
                    </Avatar>
                    <ResponsibleInfo>
                      <ResponsibleName>{selectedCity.responsible || 'Sem responsável'}</ResponsibleName>
                      <ResponsibleRole>{selectedCity.responsible ? 'Coordenador Local' : isAdmin ? 'Clique para atribuir' : 'Não atribuído'}</ResponsibleRole>
                    </ResponsibleInfo>
                    {(selectedCity.responsible || isAdmin) && (
                      <svg 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        viewBox="0 0 24 24"
                        style={{ width: '20px', height: '20px', color: '#94a3b8' }}
                      >
                        <path d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </ResponsibleCard>
                )}
              </InfoSection>
              
              {/* Additional Info */}
              <InfoSection>
                <InfoLabel>Engajamento</InfoLabel>
                <InfoCard>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '4px',
                      fontSize: '13px'
                    }}>
                      <span style={{ color: '#64748b' }}>Taxa de Conversão ({selectedCity.pastCampaigns[selectedCity.pastCampaigns.length - 1].year})</span>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>
                        {Math.round((selectedCity.pastCampaigns[selectedCity.pastCampaigns.length - 1].votes / selectedCity.population) * 100)}%
                      </span>
                    </div>
                    <div style={{ 
                      height: '8px', 
                      background: '#e2e8f0', 
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ 
                        height: '100%', 
                        width: `${Math.round((selectedCity.pastCampaigns[selectedCity.pastCampaigns.length - 1].votes / selectedCity.population) * 100)}%`,
                        background: 'var(--accent)'
                      }}></div>
                    </div>
                  </div>
                </InfoCard>
              </InfoSection>
            </SideSheetContent>
          </>
        )}
      </SideSheet>,
      document.body
      )}
      
      {/* Business Indicators Panel */}
      {showProjections && <BusinessIndicators $visible={true}>
        <IndicatorsTitle>
          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
          </svg>
          Projeção {nextElectionYear ?? ''}
        </IndicatorsTitle>

        {loadingProjections ? (
          <>
            <TotalVotes style={{ background: '#c7d9f5' }}>
              <TotalVotesLabel style={{ opacity: 0.6 }}>TOTAL PROJETADO</TotalVotesLabel>
              <ShimmerBlock $width="120px" $height="36px" $radius="8px" style={{ marginTop: 4 }} />
            </TotalVotes>
            <RegionVotesList>
              {[1, 2, 3, 4, 5, 6, 7].map(i => (
                <RegionVoteItem key={i} $color="rgba(226,232,240,0.5)" $isHovered={false} $isSelected={false}
                  style={{ cursor: 'default', pointerEvents: 'none' }}>
                  <RegionVoteName>
                    <ShimmerBlock $width="10px" $height="10px" $radius="50%" style={{ flexShrink: 0 }} />
                    <ShimmerBlock $width={`${80 + i * 8}px`} $height="13px" />
                  </RegionVoteName>
                  <ShimmerBlock $width="48px" $height="15px" $radius="6px" />
                </RegionVoteItem>
              ))}
            </RegionVotesList>
          </>
        ) : regionProjections && Object.keys(regionProjections).length > 0 ? (() => {
          const total = totalProjection ?? Object.values(regionProjections).reduce((a, b) => a + b, 0);
          return (
            <>
              <TotalVotes>
                <TotalVotesLabel>
                  TOTAL PROJETADO{nextElectionYear ? ` • ${nextElectionYear}` : ''}
                </TotalVotesLabel>
                <TotalVotesValue>
                  {total.toLocaleString('pt-BR')}
                </TotalVotesValue>
              </TotalVotes>

              <RegionVotesList>
                {Object.entries(regionProjections)
                  .sort((a, b) => b[1] - a[1])
                  .map(([regionName, votes]) => {
                    let color: string;
                    if (useCustomRegions) {
                      // Use graph-colored index from cache if available
                      const cached = voronoiCacheRef.current;
                      const meta = cached?.regionMeta.find(m => m.regionName === regionName);
                      const colorIndex = meta ? meta.colorIndex : 0;
                      color = CUSTOM_REGION_COLORS[colorIndex % CUSTOM_REGION_COLORS.length];
                    } else {
                      color = REGION_COLORS[regionName] || 'rgba(100, 116, 139, 0.15)';
                    }
                    return (
                      <RegionVoteItem
                        key={regionName}
                        $color={color}
                        $isHovered={hoveredRegion === regionName}
                        $isSelected={selectedRegion === regionName}
                        onMouseEnter={() => setHoveredRegion(regionName)}
                        onMouseLeave={() => setHoveredRegion(null)}
                        onClick={() => zoomToRegion(regionName)}
                      >
                        <RegionVoteName>
                          <RegionColorDot $color={color} />
                          {regionName.replace(' Rio-grandense', '').replace('Metropolitana de ', '')}
                        </RegionVoteName>
                        <RegionVoteValue>
                          {votes.toLocaleString('pt-BR')}
                        </RegionVoteValue>
                      </RegionVoteItem>
                    );
                  })}
              </RegionVotesList>
            </>
          );
        })() : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 8, padding: '24px 0', color: '#94a3b8', textAlign: 'center'
          }}>
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ opacity: 0.4 }}>
              <path d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
            </svg>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>
              Nenhuma projeção cadastrada
            </span>
            <span style={{ fontSize: '11px', lineHeight: 1.4 }}>
              Abra uma cidade e defina a meta de votos para a próxima eleição.
            </span>
          </div>
        )}
      </BusinessIndicators>}
    </MapContainer>
  );
};

export default CanvasMap;
